import { createApi } from '@reduxjs/toolkit/query/react';
import {
  InvitedUser,
  PlatformPreferenceData,
  PlatformUser,
  SaaSPreferenceData,
  SaaSUser,
  User,
  UserUpdateProps,
} from '@scaleup/models';

import { baseQuery } from './baseQuery';
import { TagTypes } from './tagTypes';

export const userApi = createApi({
  reducerPath: 'userApi',
  baseQuery: baseQuery,
  tagTypes: [TagTypes.user, TagTypes.users],
  endpoints: (builder) => ({
    getUser: builder.query<SaaSUser | PlatformUser, void>({
      query: () => 'user',
      providesTags: [TagTypes.user],
    }),
    getUsers: builder.query<(SaaSUser | PlatformUser)[], void>({
      query: () => 'users',
      providesTags: [TagTypes.users],
    }),
    updateUserAttribute: builder.mutation<
      User,
      {
        attribute: keyof UserUpdateProps;
        value: UserUpdateProps[keyof UserUpdateProps];
      }
    >({
      query: ({ attribute, value }) => ({
        url: 'user',
        method: 'PUT',
        body: { [attribute]: value },
      }),
      invalidatesTags: [TagTypes.user], // Updated to use the correct tag type
    }),
    updateUser: builder.mutation<SaaSUser | PlatformUser, UserUpdateProps>({
      query: (props) => ({
        url: 'user',
        method: 'PUT',
        body: props,
      }),
      invalidatesTags: [TagTypes.user, TagTypes.users], // Updated to use the correct tag type
    }),
    validateAccessCode: builder.mutation<
      {
        url: string;
      },
      {
        email: string;
        accessCode: string;
      }
    >({
      query: (props) => ({
        url: 'user/access_code',
        method: 'POST',
        body: props,
      }),
    }),
    updateUserPreferences: builder.mutation<
      SaaSUser | PlatformUser,
      SaaSPreferenceData | PlatformPreferenceData
    >({
      query: (props) => ({
        url: 'user/preferences',
        method: 'PUT',
        body: props,
      }),
      invalidatesTags: [TagTypes.user, TagTypes.users], // Updated to use the correct tag type
    }),
    completeSupplierIntake: builder.mutation<void, void>({
      query: () => ({
        url: 'user/preferences/complete_supplier_intake',
        method: 'PUT'
      }),
      invalidatesTags: [TagTypes.user, TagTypes.users], // Updated to use the correct tag type
    }),
    createUserInvites: builder.mutation<void, InvitedUser[]>({
      query: (props) => ({
        url: '/users/invite',
        method: 'POST',
        body: props,
      }),
      invalidatesTags: [TagTypes.users],
    }),
  }),
});

export const {
  useGetUserQuery,
  useUpdateUserAttributeMutation,
  useUpdateUserMutation,
  useUpdateUserPreferencesMutation,
  useCompleteSupplierIntakeMutation,
  useGetUsersQuery,
  useCreateUserInvitesMutation,
  useValidateAccessCodeMutation,
} = userApi;
